import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { TitlePageDashboard } from "../../../components/button/TitlePageDashboard";
import { ImageInput } from "../../../components/dashboard_supplier/ImageInput";
import { ListTempVariant } from "../../../components/dashboard_supplier/ListTempVariant";
import { MultipleImageInput } from "../../../components/dashboard_supplier/MultipleImageInput";
import { ButtonInput } from "../../../components/input/ButtonInput";
import { NumberInput } from "../../../components/input/NumberInput";
import { CardSkeleton } from "../../../components/skeleton/CardSkeleton";
import {
  ProductInterface,
  ProductVariantInterface,
} from "../../../constants/Product";
import { useBeforeUnload } from "../../../lib/useBeforeUnload";
import useCustomToast from "../../../lib/useCustomToast";
import { useProductVariantStore } from "../../../store/useProductVariantStore";
import { useDarkWhiteColor } from "../../../themes/colors";
import { axiosSupplier } from "../../../utils/AxiosInstance";
import { isValidUrl } from "../../../utils/helperFunction";

const SelectUnit = lazy(
  () => import("../../../components/dashboard_supplier/SelectUnit")
);

const SelectCategoryInput = lazy(
  () => import("../../../components/dashboard_supplier/SelectCategoryInput")
);

export const EditProductPage = () => {
  const [data, setData] = useState<ProductInterface | undefined>(undefined);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [resMainImage, setResMainImage] = useState<string | undefined>(
    undefined
  );
  const [deletedImages, setDeletedImages] = useState<string[]>([]);

  const [resPictures, setResPictures] = useState<string[] | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(false);
  const { id } = useParams();
  const bgBnw = useDarkWhiteColor();
  const toast = useCustomToast();
  const { variants, removeVariant, addVariant, clearVariant } =
    useProductVariantStore();
  const navigate = useNavigate();
  useBeforeUnload();

  useEffect(() => {
    axiosSupplier
      .get(`/v1/supplier/product/detail-product/${id}`)
      .then((res) => {
        setData(res.data.data);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [id]);

  // add variant when first load
  useEffect(() => {
    if (data && data.variants) {
      data.variants.map((item: ProductVariantInterface, i) => {
        const valueVariant: ProductVariantInterface = {
          id: item.id, //item.id,
          tempId: item.id ? item.id : variants.length + 1,
          name: item.name,
          price: item.price,
          stock: item.stock,
          percentage: item.percentage,
          pivot: {
            product_id: data.id,
            variant_id: item.id,
          },
        };

        addVariant(valueVariant);
      });
    } else {
      clearVariant();
    }
  }, [data?.variants]);

  useEffect(() => {
    formik.setFieldValue("variants", variants);
  }, [variants]);

  useEffect(() => {
    formik.setFieldValue("removedImages", deletedImages);
  }, [deletedImages]);

  // set main image and pictures to state
  useEffect(() => {
    if (data && data.mainImage) {
      setResMainImage(data.mainImage);
    }

    if (data && data.pictures) {
      setResPictures(data.pictures);
    }
  }, [data?.mainImage, data?.pictures]);

  const handleRemoveVariant = (variantId: any) => {
    removeVariant(variantId);
  };

  const initialValues = {
    name: data && data.name,
    category: data && data.categoryId,
    description: data && data.description,
    weight: data && data.weight,
    weightType: data && data.unit,
    mainImage: data && data.mainImage && data.mainImage,
    images: data && data.pictures && data.pictures,
    addedImages: undefined,
    removedImages: undefined,
    variants: data && data.variants,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      name: yup.string().required("Nama produk harus diisi"),
      category: yup.string().required("Kategori harus diisi"),
      description: yup.string().required("Deskripsi harus diisi"),
      weight: yup.string().required("Berat harus diisi"),
      weightType: yup.string().required("Jenis berat harus diisi"),
      mainImage: yup.mixed().required("Foto utama harus diisi"),
      variants: yup
        .array()
        .of(
          yup.object().shape({
            // id: yup.string().required("ID varian harus diisi"),
            name: yup.string().required("Nama varian harus diisi"),
            price: yup.string().required("Harga varian harus diisi"),
            stock: yup.string().required("Stok varian harus diisi"),
          })
        )
        .min(1, "Varian produk harus diisi")
        .required("Varian harus diisi"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      const formData = new FormData();
      formData.append("productId", id || "");
      formData.append("name", values.name || "");
      formData.append("category", values.category || "");
      formData.append("description", values.description || "");
      formData.append("weight", values.weight || "");
      formData.append("unit", values.weightType || "");
      formData.append("variants", JSON.stringify(values.variants || []));
      formData.append("main_picture", values.mainImage || "");
      formData.append(
        "removedImages",
        JSON.stringify(values.removedImages || [])
      );
      ((values.addedImages && (values.addedImages as File[])) || []).forEach(
        (image) => {
          formData.append("addedImages[]", image);
        }
      );

      axiosSupplier
        .post(`/v1/supplier/product/edit-product`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          toast({
            title: res.data.message,
            status: "success",
          });

          clearVariant();
          navigate("/supplier/mystore", { replace: true });
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <VStack className="add-product-page" w={"100%"} p={4} align={"stretch"}>
      <TitlePageDashboard title="Tambah Produk" isSubPage={true} />

      <form
        id="editProductForm"
        onSubmit={formik.handleSubmit}
        style={{ width: "100%" }}
      >
        <Stack
          className="root-add-product"
          gap={4}
          direction={["column-reverse", null, null, "row", "row"]}
        >
          <VStack
            className="left-container"
            gap={4}
            flex={[1, 1, 1, 1, 2]}
            align={"stretch"}
          >
            <VStack
              className="product-information"
              w={"100%"}
              p={4}
              gap={6}
              bg={bgBnw}
              borderRadius={"md"}
              align={"stretch"}
            >
              <Text textStyle={"b-reg-sb"}>Informasi Produk</Text>

              <FormControl
                isInvalid={
                  formik.errors.name && formik.touched.name ? true : false
                }
              >
                <FormLabel htmlFor="name">Nama Produk</FormLabel>

                {!loaded ? (
                  <CardSkeleton row={1} height={40} />
                ) : (
                  <Input
                    name="name"
                    type="text"
                    onChange={formik.handleChange}
                    value={formik.values.name || ""}
                  />
                )}
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={
                  formik.errors.category && formik.touched.category
                    ? true
                    : false
                }
              >
                <FormLabel htmlFor="category">Kategori</FormLabel>

                <Suspense fallback={<CardSkeleton row={1} height={40} />}>
                  {!loaded ? (
                    <CardSkeleton row={1} height={40} />
                  ) : (
                    <SelectCategoryInput
                      inputValue={formik.values.category}
                      onSelected={(id) => {
                        formik.setFieldValue("category", id);
                      }}
                    />
                  )}
                </Suspense>

                <FormErrorMessage>
                  {formik.errors.category?.toString()}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={
                  formik.errors.description && formik.touched.description
                    ? true
                    : false
                }
              >
                <FormLabel htmlFor="description">Deskripsi Produk</FormLabel>

                <Textarea
                  name="description"
                  onChange={formik.handleChange}
                  value={formik.values.description || ""}
                />
                <FormErrorMessage>{formik.errors.description}</FormErrorMessage>
              </FormControl>

              <HStack gap={6}>
                <FormControl
                  isInvalid={
                    formik.errors.weight && formik.touched.weight ? true : false
                  }
                >
                  <FormLabel htmlFor="weight">Berat Produk</FormLabel>

                  {!loaded ? (
                    <CardSkeleton row={1} height={40} />
                  ) : (
                    <NumberInput
                      name="weight"
                      inputValue={formik.values.weight}
                      onConfirm={(input) => {
                        formik.setFieldValue("weight", input);
                      }}
                    />
                  )}
                  <FormErrorMessage>{formik.errors.weight}</FormErrorMessage>
                </FormControl>

                <FormControl
                  isInvalid={
                    formik.errors.weightType && formik.touched.weightType
                      ? true
                      : false
                  }
                >
                  <FormLabel htmlFor="weightType">Jenis Berat</FormLabel>

                  <Suspense fallback={<CardSkeleton row={1} height={40} />}>
                    {!loaded ? (
                      <CardSkeleton row={1} height={40} />
                    ) : (
                      <SelectUnit
                        inputValue={
                          formik.values.weightType &&
                          (formik.values.weightType as string)
                        }
                        onSelected={(id: string) => {
                          formik.setFieldValue("weightType", id);
                        }}
                      />
                    )}
                  </Suspense>

                  <FormErrorMessage>
                    {formik.errors.weightType}
                  </FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>

            <VStack
              className="root-variant"
              w={"100%"}
              p={4}
              bg={bgBnw}
              borderRadius={"md"}
              align={"stretch"}
            >
              <Text textStyle={"b-sm"} fontWeight={"semibold"}>
                Varian Produk
              </Text>

              <FormControl
                isInvalid={
                  formik.errors.variants && formik.touched.variants
                    ? true
                    : false
                }
              >
                <ButtonInput
                  isError={
                    !!formik.errors.variants && !!formik.touched.variants
                  }
                />
                <FormErrorMessage>{formik.errors.variants}</FormErrorMessage>
              </FormControl>

              {variants && variants.length > 0 && (
                <>
                  <Text textStyle={"b-reg-sb"} mt={4} px={2}>
                    Varian yang ditambahkan
                  </Text>

                  <ListTempVariant />
                </>
              )}
            </VStack>

            <Button
              w={"fit-content"}
              type="button"
              spinnerPlacement="start"
              isLoading={loading}
              loadingText="Loading"
              colorScheme="red"
              onClick={() => {
                formik.handleSubmit();
                formik.setFieldTouched("mainImage", true);
              }}
            >
              Edit Produk
            </Button>
          </VStack>

          <VStack className="right-container" gap={4} flex={[1, 1, 1, 1, 1]}>
            <VStack
              className="wrap-main-image"
              w={"100%"}
              p={4}
              gap={4}
              bg={bgBnw}
              borderRadius={"md"}
              align={"stretch"}
            >
              <VStack
                className="wrap-title-main-image"
                w={"100%"}
                gap={0}
                align={"stretch"}
              >
                <Text textStyle={"b-reg-sb"}>Foto Utama</Text>

                <Text fontSize={[8, null, 10]} as={"i"} variant={"secondary"}>
                  Foto yang akan dipakai untuk thumbnail dari product anda
                </Text>
              </VStack>

              <FormControl
                isInvalid={
                  formik.errors.mainImage && formik.touched.mainImage
                    ? true
                    : false
                }
              >
                {!loaded ? (
                  <CardSkeleton row={1} height={180} />
                ) : (
                  <ImageInput
                    inputName="mainImage"
                    initValue={`${process.env.REACT_APP_MEDIA_URL}${resMainImage}`}
                    isError={
                      !!formik.errors.mainImage && !!formik.touched.mainImage
                    }
                    onFileChange={async (img) => {
                      formik.setFieldTouched("mainImage", true);
                      await Promise.resolve();

                      formik.setFieldValue("mainImage", img);
                    }}
                    onHandleDrop={async (img) => {
                      formik.setFieldTouched("mainImage", true);
                      await Promise.resolve();
                      formik.setFieldValue("mainImage", img);
                    }}
                  />
                )}

                <FormErrorMessage>{formik.errors.mainImage}</FormErrorMessage>
              </FormControl>
            </VStack>

            <VStack
              className="wrap-images"
              w={"100%"}
              p={4}
              gap={4}
              bg={bgBnw}
              borderRadius={"md"}
              align={"stretch"}
            >
              <VStack
                className="wrap-title-images"
                w={"100%"}
                gap={0}
                align={"stretch"}
              >
                <Text textStyle={"b-reg-sb"}>Foto Lainnya</Text>
              </VStack>

              {!loaded ? (
                <CardSkeleton row={1} height={285} />
              ) : (
                <MultipleImageInput
                  inputName="images"
                  initValues={resPictures}
                  onFilesChange={(img) => {
                    formik.setFieldValue("addedImages", img);
                    if (img?.length === 0) {
                      formik.setFieldValue("removedImages", resPictures);
                    }
                  }}
                  onDeletedImg={(
                    index: number | undefined,
                    img: string | undefined
                  ) => {
                    if (img && isValidUrl(img)) {
                      setDeletedImages((prevImg) => [...prevImg, img]);
                    }
                  }}
                />
              )}
            </VStack>
          </VStack>
        </Stack>
      </form>
    </VStack>
  );
};
